import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { STATE_TAX_DATA } from '../../lib/calculations'
import { calculateBonusRsuTax } from '../../lib/calculations/bonusRsuTax'
import { trackExportReport } from '../../lib/analytics'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', step, min, max, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function BonusRsuTax() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [bonusAmount, setBonusAmount] = useState(15000)
  const [regularIncome, setRegularIncome] = useState(85000)
  const [filingStatus, setFilingStatus] = useState('single')
  const [stateCode, setStateCode] = useState('OH')
  const [isRSU, setIsRSU] = useState(false)

  const result = useMemo(() => calculateBonusRsuTax({
    bonusAmount, filingStatus, regularIncome, stateCode, isRSU,
  }), [bonusAmount, regularIncome, filingStatus, stateCode, isRSU])

  const filingStatusLabels = { single: 'Single', mfj: 'Married Filing Jointly', hoh: 'Head of Household' }

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14
    const contentWidth = pageWidth - margin * 2
    const incomeType = isRSU ? 'RSU' : 'Bonus'

    const checkPageBreak = (y, needed = 40) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return y
    }

    // ── Title ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text(`${incomeType} Tax Report`, margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(200, 200, 200)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Input Summary ──
    let y = 40
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text(isRSU ? 'RSU Vesting Details' : 'Bonus Details', margin, y)
    y += 4

    const stateEntry = STATE_TAX_DATA.find(s => s.code === stateCode)
    const stateName = stateEntry ? `${stateEntry.name} (${stateEntry.rate}%)` : stateCode

    autoTable(doc, {
      startY: y,
      head: [['Input', 'Value']],
      body: [
        ['Income Type', isRSU ? 'RSU Vest' : 'Cash Bonus'],
        [isRSU ? 'RSU Value at Vest' : 'Bonus Amount', formatCurrency(bonusAmount)],
        ['Regular Annual Income (Base Salary)', formatCurrency(regularIncome)],
        ['Total Combined Income', formatCurrency(regularIncome + bonusAmount)],
        ['Filing Status', filingStatusLabels[filingStatus] || filingStatus],
        ['State', stateName],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 2: Key Results ──
    y = (doc.lastAutoTable?.finalY || 90) + 12
    y = checkPageBreak(y, 60)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: [
        ['Take-Home Pay', formatCurrency(result.actualTakeHome)],
        [`Effective Tax Rate on ${incomeType}`, formatPercent(result.effectiveBonusRate)],
        ['Marginal Federal Tax Rate', `${result.marginalRate}%`],
        ['Employer Withholding (Flat 22%)', formatCurrency(result.flatTotalWithholding)],
        ['Actual Total Tax Owed', formatCurrency(result.actualTotalTax)],
        [
          result.isNegligibleDifference ? 'Withholding Status' : result.willGetRefund ? 'Expected Refund' : 'Additional Owed at Filing',
          result.isNegligibleDifference ? 'Properly Withheld' : formatCurrency(Math.abs(result.totalDifference)),
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 3: Flat Withholding Breakdown ──
    y = (doc.lastAutoTable?.finalY || 130) + 12
    y = checkPageBreak(y, 60)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Flat Withholding (22%) — What Your Employer Withholds', margin, y)
    y += 2
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text(`What your employer withholds from the ${isRSU ? 'vest' : 'bonus'}`, margin, y + 4)
    y += 8

    const flatRows = [
      ['Federal (22% flat)', formatCurrency(result.flatFederalWithholding)],
    ]
    if (result.flatStateWithholding > 0) {
      flatRows.push([`State (${result.stateRate}%)`, formatCurrency(result.flatStateWithholding)])
    }
    flatRows.push(
      ['FICA (Social Security + Medicare)', formatCurrency(result.flatFICA)],
      ['Total Withheld', formatCurrency(result.flatTotalWithholding)],
      ['Take-Home After Withholding', formatCurrency(result.flatTakeHome)],
    )

    autoTable(doc, {
      startY: y,
      head: [['Component', 'Amount']],
      body: flatRows,
      theme: 'grid',
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [255, 251, 235] },
      margin: { left: margin, right: margin },
    })

    // ── Section 4: Actual Tax Owed Breakdown ──
    y = (doc.lastAutoTable?.finalY || 170) + 12
    y = checkPageBreak(y, 60)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text(`Actual Tax Owed — Based on Your Marginal Rate (${result.marginalRate}%)`, margin, y)
    y += 2
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text(`Based on your marginal rate (${result.marginalRate}%)`, margin, y + 4)
    y += 8

    const actualRows = [
      [`Federal (${result.marginalRate}% marginal)`, formatCurrency(result.actualFederalTax)],
    ]
    if (result.actualStateTax > 0) {
      actualRows.push([`State (${result.stateRate}%)`, formatCurrency(result.actualStateTax)])
    }
    actualRows.push(
      ['FICA (Social Security + Medicare)', formatCurrency(result.actualFICA)],
      ['Total Owed', formatCurrency(result.actualTotalTax)],
      ['True Take-Home', formatCurrency(result.actualTakeHome)],
    )

    autoTable(doc, {
      startY: y,
      head: [['Component', 'Amount']],
      body: actualRows,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 5: Side-by-Side Withholding vs Actual Comparison ──
    y = (doc.lastAutoTable?.finalY || 200) + 12
    y = checkPageBreak(y, 70)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('Withholding vs Actual Tax — Side-by-Side', margin, y)
    y += 4

    const comparisonRows = [
      ['Federal Tax', formatCurrency(result.flatFederalWithholding), formatCurrency(result.actualFederalTax), formatCurrency(result.flatFederalWithholding - result.actualFederalTax)],
      ['State Tax', formatCurrency(result.flatStateWithholding), formatCurrency(result.actualStateTax), formatCurrency(result.flatStateWithholding - result.actualStateTax)],
      ['FICA', formatCurrency(result.flatFICA), formatCurrency(result.actualFICA), formatCurrency(0)],
      ['Total', formatCurrency(result.flatTotalWithholding), formatCurrency(result.actualTotalTax), formatCurrency(result.totalDifference)],
      ['Take-Home', formatCurrency(result.flatTakeHome), formatCurrency(result.actualTakeHome), formatCurrency(result.actualTakeHome - result.flatTakeHome)],
    ]

    autoTable(doc, {
      startY: y,
      head: [['Component', 'Flat Withholding (22%)', `Actual (${result.marginalRate}% marginal)`, 'Difference']],
      body: comparisonRows,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
    })

    // ── Section 6: Refund / Owe Status Banner ──
    y = (doc.lastAutoTable?.finalY || 230) + 10
    y = checkPageBreak(y, 30)

    if (result.isNegligibleDifference) {
      doc.setFillColor(239, 246, 255)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F')
      doc.setDrawColor(59, 130, 246)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'S')
      doc.setFontSize(10)
      doc.setTextColor(30, 64, 175)
      doc.text('The 22% flat rate closely matches your actual tax liability. No significant refund or additional payment expected.', margin + 6, y + 11)
      y += 24
    } else if (result.willGetRefund) {
      doc.setFillColor(240, 253, 244)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'F')
      doc.setDrawColor(34, 197, 94)
      doc.roundedRect(margin, y, contentWidth, 18, 3, 3, 'S')
      doc.setFontSize(10)
      doc.setTextColor(22, 101, 52)
      doc.text(`Your employer will over-withhold by ${formatCurrency(Math.abs(result.totalDifference))}. You'll get this back as a tax refund.`, margin + 6, y + 11)
      y += 24
    } else {
      doc.setFillColor(254, 242, 242)
      doc.roundedRect(margin, y, contentWidth, 24, 3, 3, 'F')
      doc.setDrawColor(220, 38, 38)
      doc.roundedRect(margin, y, contentWidth, 24, 3, 3, 'S')
      doc.setFontSize(10)
      doc.setTextColor(153, 27, 27)
      doc.text(`Your employer will under-withhold by ${formatCurrency(Math.abs(result.totalDifference))}. You may owe this at tax time.`, margin + 6, y + 10)
      if (result.marginalRate > 22) {
        doc.setFontSize(8.5)
        doc.setTextColor(180, 60, 60)
        doc.text(`Your marginal rate (${result.marginalRate}%) exceeds the 22% flat withholding rate. Consider making an estimated tax payment.`, margin + 6, y + 18)
      }
      y += 30
    }

    // ── Section 7: FICA Breakdown Detail ──
    y = checkPageBreak(y, 50)
    doc.setFontSize(14)
    doc.setTextColor(30, 30, 30)
    doc.text('FICA Tax Detail', margin, y)
    y += 4

    const ficaRows = [
      ['Social Security Tax (6.2%)', formatCurrency(result.bonusSSTax)],
      ['Medicare Tax (1.45%)', formatCurrency(result.bonusMedicareTax)],
    ]
    if (result.additionalMedicare > 0) {
      ficaRows.push(['Additional Medicare Tax (0.9%)', formatCurrency(result.additionalMedicare)])
    }
    ficaRows.push(['Total FICA', formatCurrency(result.flatFICA)])

    autoTable(doc, {
      startY: y,
      head: [['FICA Component', 'Amount']],
      body: ficaRows,
      theme: 'grid',
      headStyles: { fillColor: [107, 114, 128], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      bodyStyles: { fontSize: 9.5, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })

    // ── Section 8: RSU Explanation (conditional) ──
    if (isRSU) {
      y = (doc.lastAutoTable?.finalY || y) + 12
      y = checkPageBreak(y, 40)
      doc.setFontSize(14)
      doc.setTextColor(30, 30, 30)
      doc.text('How RSU Taxation Works', margin, y)
      y += 7
      doc.setFontSize(9.5)
      doc.setTextColor(60, 60, 60)
      const rsuText = doc.splitTextToSize(
        'RSUs are taxed as ordinary income at the time of vesting \u2014 the same way bonuses are taxed. Your employer withholds taxes on the vest value using the same 22% supplemental flat rate. The fair market value on the vest date becomes your cost basis for future capital gains calculations if you hold and later sell the shares.',
        contentWidth
      )
      doc.text(rsuText, margin, y)
      y += rsuText.length * 4.5
    }

    // ── Disclaimer ──
    y = (doc.lastAutoTable?.finalY || y) + 10
    y = checkPageBreak(y, 25)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    const disclaimerLines = doc.splitTextToSize(
      `Disclaimer: This report is for informational purposes only and does not constitute tax advice. ${incomeType} tax calculations are estimates based on 2025 federal tax brackets, the IRS 22% supplemental wage flat withholding rate, and state flat rates. Actual tax liability may vary based on your complete tax situation. Consult a qualified tax professional for personalized advice.`,
      contentWidth
    )
    doc.text(disclaimerLines, margin, y)

    doc.save(`${isRSU ? 'rsu' : 'bonus'}-tax-${Date.now()}.pdf`)
  }

  const filingOptions = [
    { value: 'single', label: 'Single' },
    { value: 'mfj', label: 'MFJ' },
    { value: 'hoh', label: 'HOH' },
  ]

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">
            {isRSU ? 'RSU Vesting Details' : 'Bonus Details'}
          </h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Type</label>
            <div className="flex gap-1">
              {[{ value: false, label: 'Cash Bonus' }, { value: true, label: 'RSU Vest' }].map(opt => (
                <button
                  key={String(opt.value)}
                  onClick={() => setIsRSU(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    isRSU === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={isRSU === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <InputField label={isRSU ? 'RSU Value at Vest' : 'Bonus Amount'} value={bonusAmount} onChange={setBonusAmount} prefix="$" />
          <InputField label="Regular Annual Income" value={regularIncome} onChange={setRegularIncome} prefix="$" hint="Your base salary before bonus" />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Filing Status</label>
            <div className="flex gap-1">
              {filingOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilingStatus(opt.value)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    filingStatus === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={filingStatus === opt.value ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">State</label>
            <select
              value={stateCode}
              onChange={e => setStateCode(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            >
              {STATE_TAX_DATA.filter(s => s.code !== 'CUSTOM').map(s => (
                <option key={s.code} value={s.code}>{s.name} ({s.rate}%)</option>
              ))}
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Take-Home Pay" value={formatCurrency(result.actualTakeHome)} color={colors.positive} highlight />
        <ResultCard label="Effective Rate" value={formatPercent(result.effectiveBonusRate)} color={colors.primary} />
        <ResultCard label="Employer Withholds" value={formatCurrency(result.flatTotalWithholding)} color={colors.warning} />
        <ResultCard
          label={result.isNegligibleDifference ? 'Withholding Status' : result.willGetRefund ? 'Expected Refund' : 'Additional Owed'}
          value={result.isNegligibleDifference ? 'Properly Withheld' : formatCurrency(Math.abs(result.totalDifference))}
          color={result.isNegligibleDifference ? colors.primary : result.willGetRefund ? colors.positive : colors.negative}
        />
      </div>

      {/* Withholding vs Actual */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Withholding vs Actual Tax</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Flat Withholding */}
          <div className="p-4 rounded-xl border border-card">
            <h4 className="text-sm font-semibold text-heading mb-3">Flat Withholding (22%)</h4>
            <p className="text-xs text-muted mb-3">What your employer withholds from the {isRSU ? 'vest' : 'bonus'}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted">Federal (22% flat)</span><span className="text-heading">{formatCurrency(result.flatFederalWithholding)}</span></div>
              {result.flatStateWithholding > 0 && <div className="flex justify-between"><span className="text-muted">State ({result.stateRate}%)</span><span className="text-heading">{formatCurrency(result.flatStateWithholding)}</span></div>}
              <div className="flex justify-between"><span className="text-muted">FICA</span><span className="text-heading">{formatCurrency(result.flatFICA)}</span></div>
              <div className="border-t border-card pt-2 flex justify-between font-semibold">
                <span>Total Withheld</span>
                <span style={{ color: colors.warning }}>{formatCurrency(result.flatTotalWithholding)}</span>
              </div>
              <div className="flex justify-between text-muted"><span>Take-Home</span><span>{formatCurrency(result.flatTakeHome)}</span></div>
            </div>
          </div>

          {/* Actual Liability */}
          <div className="p-4 rounded-xl border border-card">
            <h4 className="text-sm font-semibold text-heading mb-3">Actual Tax Owed</h4>
            <p className="text-xs text-muted mb-3">Based on your marginal rate ({result.marginalRate}%)</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted">Federal ({result.marginalRate}% marginal)</span><span className="text-heading">{formatCurrency(result.actualFederalTax)}</span></div>
              {result.actualStateTax > 0 && <div className="flex justify-between"><span className="text-muted">State ({result.stateRate}%)</span><span className="text-heading">{formatCurrency(result.actualStateTax)}</span></div>}
              <div className="flex justify-between"><span className="text-muted">FICA</span><span className="text-heading">{formatCurrency(result.actualFICA)}</span></div>
              <div className="border-t border-card pt-2 flex justify-between font-semibold">
                <span>Total Owed</span>
                <span style={{ color: colors.primary }}>{formatCurrency(result.actualTotalTax)}</span>
              </div>
              <div className="flex justify-between text-muted"><span>True Take-Home</span><span>{formatCurrency(result.actualTakeHome)}</span></div>
            </div>
          </div>
        </div>

        {/* Refund/Owe Banner */}
        {result.isNegligibleDifference ? (
          <div className="mt-4 p-3 rounded-xl" style={{
            background: `${colors.primary}15`,
            border: `1px solid ${colors.primary}33`,
          }}>
            <p className="text-sm" style={{ color: colors.primary }}>
              The 22% flat rate closely matches your actual tax liability. No significant refund or additional payment expected.
            </p>
          </div>
        ) : (
          <div className="mt-4 p-3 rounded-xl" style={{
            background: result.willGetRefund ? `${colors.positive}15` : `${colors.negative}15`,
            border: `1px solid ${result.willGetRefund ? colors.positive : colors.negative}33`,
          }}>
            <p className="text-sm" style={{ color: result.willGetRefund ? colors.positive : colors.negative }}>
              {result.willGetRefund
                ? `Your employer will over-withhold by ${formatCurrency(Math.abs(result.totalDifference))}. You'll get this back as a tax refund.`
                : `Your employer will under-withhold by ${formatCurrency(Math.abs(result.totalDifference))}. You may owe this at tax time.`
              }
              {result.marginalRate > 22 && !result.willGetRefund && (
                <span className="block mt-1 text-xs opacity-80">
                  Your marginal rate ({result.marginalRate}%) exceeds the 22% flat withholding rate. Consider making an estimated tax payment.
                </span>
              )}
            </p>
          </div>
        )}
      </GlassCard>

      {/* RSU Note */}
      {result.isRSU && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-heading mb-2">How RSU Taxation Works</h3>
          <p className="text-sm text-muted">
            RSUs are taxed as ordinary income at the time of vesting — the same way bonuses are taxed. Your employer withholds taxes on the vest value using the same 22% supplemental flat rate. The fair market value on the vest date becomes your <strong>cost basis</strong> for future capital gains calculations if you hold and later sell the shares.
          </p>
        </GlassCard>
      )}
    </div>
  )
}
